import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminAchievementsApi,
  type Achievement,
  type AchievementRule,
} from '../../services/achievements.service';

const EMPTY_RULE = `{
  "type": "event_first",
  "event": "cv.uploaded"
}`;

type FormState = {
  id?: string;
  key: string;
  title: string;
  description: string;
  tier: number;
  order: number;
  iconUrl: string;
  isActive: boolean;
  ruleJson: string;
};

const EMPTY_FORM: FormState = {
  key: '',
  title: '',
  description: '',
  tier: 1,
  order: 0,
  iconUrl: '',
  isActive: true,
  ruleJson: EMPTY_RULE,
};

function toForm(a: Achievement): FormState {
  return {
    id: a.id,
    key: a.key,
    title: a.title,
    description: a.description,
    tier: a.tier,
    order: a.order,
    iconUrl: a.iconUrl ?? '',
    isActive: a.isActive,
    ruleJson: JSON.stringify(a.rule, null, 2),
  };
}

export default function AdminAchievements() {
  const queryClient = useQueryClient();
  const [tierFilter, setTierFilter] = useState<number | undefined>(undefined);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [ruleError, setRuleError] = useState<string | null>(null);

  const { data: events } = useQuery({
    queryKey: ['admin-achievements', 'events'],
    queryFn: () => adminAchievementsApi.knownEvents(),
  });

  const { data: list = [] } = useQuery({
    queryKey: ['admin-achievements', { tierFilter }],
    queryFn: () => adminAchievementsApi.list({ tier: tierFilter }),
  });

  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof adminAchievementsApi.create>[0]) =>
      adminAchievementsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-achievements'] });
      setForm(EMPTY_FORM);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...rest }: { id: string } & Parameters<typeof adminAchievementsApi.update>[1]) =>
      adminAchievementsApi.update(id, rest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-achievements'] });
      setForm(EMPTY_FORM);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminAchievementsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-achievements'] }),
  });

  const runCronMutation = useMutation({
    mutationFn: () => adminAchievementsApi.runCron(),
  });

  const reprocessMutation = useMutation({
    mutationFn: () => adminAchievementsApi.reprocessEvents(),
  });

  const tiers = useMemo(() => {
    const set = new Set(list.map((a) => a.tier));
    return [...set].sort((a, b) => a - b);
  }, [list]);

  function tryParseRule(json: string): AchievementRule | null {
    try {
      const parsed = JSON.parse(json);
      setRuleError(null);
      return parsed as AchievementRule;
    } catch (err) {
      setRuleError((err as Error).message);
      return null;
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const rule = tryParseRule(form.ruleJson);
    if (!rule) return;
    const payload = {
      key: form.key,
      title: form.title,
      description: form.description,
      tier: form.tier,
      order: form.order,
      iconUrl: form.iconUrl || null,
      isActive: form.isActive,
      rule,
    };
    if (form.id) {
      const { key: _key, ...updateFields } = payload;
      updateMutation.mutate({ id: form.id, ...updateFields });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-on-surface">Achievements (admin)</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => reprocessMutation.mutate()}
            className="text-sm px-3 py-1.5 rounded-lg bg-surface-container text-on-surface border border-outline-variant/30 hover:bg-surface-container-high"
            disabled={reprocessMutation.isPending}
            title="Re-encola los DomainEvents pendientes (útil tras lanzar lazy assignment)"
          >
            {reprocessMutation.isPending
              ? 'Re-procesando…'
              : reprocessMutation.data
              ? `${reprocessMutation.data.reEnqueued} re-encolados`
              : 'Reprocess events'}
          </button>
          <button
            type="button"
            onClick={() => runCronMutation.mutate()}
            className="text-sm px-3 py-1.5 rounded-lg bg-primary text-on-primary hover:opacity-90"
            disabled={runCronMutation.isPending}
          >
            {runCronMutation.isPending ? 'Corriendo cron…' : 'Run cron now'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={handleSubmit} className="bg-surface-container-low rounded-xl p-5 border border-outline-variant/10 space-y-3">
          <h2 className="font-semibold text-on-surface">
            {form.id ? `Editar: ${form.key}` : 'Crear achievement'}
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-on-surface-variant">
              Key
              <input
                value={form.key}
                onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                disabled={Boolean(form.id)}
                className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface"
                required
              />
            </label>
            <label className="text-sm text-on-surface-variant">
              Tier
              <input
                type="number"
                value={form.tier}
                min={1}
                max={20}
                onChange={(e) => setForm((f) => ({ ...f, tier: Number(e.target.value) }))}
                className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface"
                required
              />
            </label>
          </div>

          <label className="block text-sm text-on-surface-variant">
            Title
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface"
              required
            />
          </label>

          <label className="block text-sm text-on-surface-variant">
            Description
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface"
              rows={2}
              required
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-on-surface-variant">
              Order
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
                className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface"
              />
            </label>
            <label className="text-sm text-on-surface-variant flex items-end gap-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>

          <label className="block text-sm text-on-surface-variant">
            Rule JSON
            <textarea
              value={form.ruleJson}
              onChange={(e) => setForm((f) => ({ ...f, ruleJson: e.target.value }))}
              onBlur={(e) => tryParseRule(e.target.value)}
              className="mt-1 w-full bg-surface-container rounded px-2 py-1 text-on-surface font-mono text-xs"
              rows={8}
              required
            />
          </label>
          {ruleError && <div className="text-error text-xs">JSON inválido: {ruleError}</div>}
          {error && <div className="text-error text-xs">{error}</div>}

          <details className="text-xs text-on-surface-variant">
            <summary className="cursor-pointer">Eventos conocidos</summary>
            <ul className="mt-2 space-y-1">
              {events?.events.map((evt) => (
                <li key={evt.type}>
                  <code className="bg-surface-container px-1 rounded">{evt.type}</code> — {evt.description}
                </li>
              ))}
            </ul>
          </details>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-sm"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {form.id ? 'Guardar cambios' : 'Crear'}
            </button>
            {form.id && (
              <button
                type="button"
                onClick={() => setForm(EMPTY_FORM)}
                className="px-3 py-1.5 rounded-lg bg-surface-container text-on-surface text-sm"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>

        <div className="bg-surface-container-low rounded-xl p-5 border border-outline-variant/10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-on-surface">Catálogo</h2>
            <select
              value={tierFilter ?? ''}
              onChange={(e) => setTierFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="bg-surface-container rounded px-2 py-1 text-sm text-on-surface"
            >
              <option value="">Todos los tiers</option>
              {tiers.map((tier) => (
                <option key={tier} value={tier}>
                  Tier {tier}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {list.map((a) => (
              <div key={a.id} className="bg-surface-container rounded-lg px-3 py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-on-surface truncate">
                    [{a.tier}] {a.title}
                  </div>
                  <div className="text-xs text-on-surface-variant truncate">
                    <code>{a.key}</code> · {a.rule.type} · {a.rule.event}
                    {a.isActive ? '' : ' · INACTIVO'}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => setForm(toForm(a))}
                    className="text-xs px-2 py-1 rounded bg-primary/10 text-primary"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Desactivar "${a.title}"?`)) deleteMutation.mutate(a.id);
                    }}
                    className="text-xs px-2 py-1 rounded bg-error/10 text-error"
                  >
                    Borrar
                  </button>
                </div>
              </div>
            ))}
            {list.length === 0 && (
              <div className="text-sm text-on-surface-variant py-4 text-center">
                Sin achievements para el filtro actual.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
